import cx from 'classnames'
import Styles from './Chip.module.scss'
import { ChipProps } from './Types'
import { useToggleState } from '@react-stately/toggle'
import { useToggleButton } from '@react-aria/button'
import React, { forwardRef, ReactNode } from 'react'
import { FocusRing } from '@react-aria/focus'
import { Icon } from 'almanac-ui/icons/Types'
import { Close } from 'blaze-ui/icons'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { usePreventScrollClick } from 'hooks/usePreventScrollClick'
import { filterDOMProps, mergeProps, useObjectRef } from '@react-aria/utils'

const FrontIcon = ({ icon }: { icon: ReactNode | Icon }) => {
  if (!icon) {
    return null
  }

  if (React.isValidElement(icon)) {
    return <>{icon}</>
  }

  const IconComponent = icon as Icon
  return <IconComponent />
}

const Chip = forwardRef<HTMLButtonElement, React.PropsWithChildren<ChipProps>>(
  (
    {
      children,
      avatar,
      className,
      deletable = true,
      size = 'md',
      variant = 'wizard',
      tooltipLabel,
      tooltipPlacement = 'top',
      contentType = 'text',
      selectable = true,
      ...props
    },
    fRef,
  ) => {
    const ref = useObjectRef(fRef)
    const state = useToggleState(props)
    const { handleTouchStart, handleTouchMove, handleTouchEnd, isDragging } = usePreventScrollClick(10)
    const { buttonProps } = useToggleButton({ ...props, isDisabled: isDragging }, state, ref)

    const chip = (
      <button
        {...mergeProps(
          buttonProps,
          filterDOMProps(props, { labelable: true, propNames: new Set(['role', 'aria-checked']) }),
          {
            onTouchStart: handleTouchStart,
            onTouchMove: handleTouchMove,
            onTouchEnd: handleTouchEnd,
          },
        )}
        className={cx(
          Styles.chip,
          Styles[size],
          Styles[variant],
          Styles[contentType],
          {
            [Styles.withFrontIcon]: Boolean(props.icon),
            [Styles.withAvatar]: Boolean(avatar),
            [Styles.selected]: selectable && state.isSelected,
            [Styles.disabled]: props.isDisabled,
          },
          className,
        )}
        ref={ref}
      >
        {props.icon && (
          <span className={Styles.icon} aria-hidden>
            <FrontIcon icon={props.icon} />
          </span>
        )}
        {avatar}
        {children}
        {deletable && (!selectable || state.isSelected) && (
          <span className={Styles.delete}>
            <Close size={12} />
          </span>
        )}
      </button>
    )

    if (!!tooltipLabel)
      return (
        <FocusRing focusRingClass={Styles.focus}>
          <Tooltip label={tooltipLabel} placement={tooltipPlacement}>
            {chip}
          </Tooltip>
        </FocusRing>
      )

    return <FocusRing focusRingClass={Styles.focus}>{chip}</FocusRing>
  },
)

export { Chip }
